/**
 * @unscoped — PayrollPage 의 i18n — Cycle 68.
 *
 * cycle 55-67 의 i18n cleanup 8 사이클 + 정찰 sub-cycle 의 후속 — cycle 67 발견된
 * false negative 5 page 중 PayrollPage (30 hardcoded) 봉합. SystemConnectingBanner 의
 * default text 도 props extension 으로 i18n 적용.
 *
 * cycle 51 calendar / cycle 54 mail / cycle 55 onboarding / cycle 57 board /
 * cycle 58 task / cycle 59-60 settings / cycle 61 chat / cycle 68 payroll = 7 신규 모듈.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 *   - params 치환 ({name}) 지원
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ===== PayrollPage 메인 =====
  'payroll.title': { ko: '급여', en: 'Payroll', ja: '給与' },
  'payroll.loading': { ko: '로딩 중…', en: 'Loading…', ja: '読み込み中...' },
  'payroll.err.list_load': {
    ko: '급여 명세 목록 로딩 실패',
    en: 'Failed to load payroll statements',
    ja: '給与明細一覧の読み込み失敗',
  },
  'payroll.err.statement_load': {
    ko: '명세서 로딩 실패',
    en: 'Failed to load statement',
    ja: '明細書の読み込み失敗',
  },

  // ===== SystemConnectingBanner (PayrollPage 사용) =====
  'payroll.banner.system_name': { ko: '급여 시스템', en: 'Payroll system', ja: '給与システム' },
  'payroll.banner.detail': {
    ko: '아직 명세서가 없거나 외부 급여 시스템 연동 대기 중입니다.',
    en: 'No statements yet or awaiting external payroll system integration.',
    ja: 'まだ明細書がないか、外部の給与システム連携待ちです。',
  },
  // ===== 빈 상태 (2026-09-13 · E) — 명세서 0건. «연동 대기» 가 아니라 «아직 없다» 와 다음에 올 일 =====
  'payroll.empty.title': {
    ko: '아직 받은 급여 명세서가 없습니다',
    en: 'No payroll statements yet',
    ja: 'まだ給与明細がありません',
  },
  'payroll.empty.desc': {
    ko: '급여가 지급되면 그 달의 명세서가 여기에 쌓입니다.',
    en: 'Each month’s statement will appear here once salary is paid.',
    ja: '給与が支払われると、その月の明細がここに表示されます。',
  },

  // ===== Sidebar =====
  'payroll.sidebar.heading': { ko: '급여 명세', en: 'Payroll statements', ja: '給与明細' },
  'payroll.sidebar.sub': { ko: '월 선택', en: 'Select month', ja: '月を選択' },
  'payroll.sidebar.latest_badge': { ko: '최신', en: 'Latest', ja: '最新' },

  // ===== Detail header =====
  'payroll.detail.title': {
    ko: '{year}-{month} 급여 명세',
    en: '{year}-{month} payroll statement',
    ja: '{year}-{month} 給与明細',
  },
  'payroll.detail.pay_date': { ko: '지급일: {date}', en: 'Pay date: {date}', ja: '支給日: {date}' },
  'payroll.detail.btn_pdf': { ko: 'PDF 저장', en: 'Save PDF', ja: 'PDF保存' },
  'payroll.detail.btn_pdf_disabled_title': {
    ko: 'PDF 발급은 다음 사이클',
    en: 'PDF export coming in a future cycle',
    ja: 'PDF発行は今後のサイクル',
  },

  // ===== Net pay card =====
  'payroll.net_card.label': { ko: '실수령액', en: 'Net pay', ja: '実支給額' },
  'payroll.net_card.unit_won': { ko: '원', en: '', ja: '円' },
  'payroll.net_card.total_earnings': { ko: '총 지급액', en: 'Total earnings', ja: '総支給額' },
  'payroll.net_card.total_deductions': { ko: '총 공제액', en: 'Total deductions', ja: '総控除額' },

  // ===== Earnings / Deductions tables =====
  'payroll.tables.earnings_heading': { ko: '지급 내역', en: 'Earnings', ja: '支給内訳' },
  'payroll.tables.deductions_heading': { ko: '공제 내역', en: 'Deductions', ja: '控除内訳' },
  'payroll.tables.total_label': { ko: '합계', en: 'Total', ja: '合計' },
  'payroll.tables.row.base_salary': { ko: '기본급', en: 'Base salary', ja: '基本給' },
  'payroll.tables.row.overtime': { ko: '연장근로수당', en: 'Overtime pay', ja: '時間外手当' },
  'payroll.tables.row.bonus': { ko: '상여금', en: 'Bonus', ja: '賞与' },
  'payroll.tables.row.income_tax': { ko: '소득세', en: 'Income tax', ja: '所得税' },
  'payroll.tables.row.national_pension': { ko: '국민연금', en: 'National pension', ja: '国民年金' },
  'payroll.tables.row.health_insurance': { ko: '건강보험', en: 'Health insurance', ja: '健康保険' },
  'payroll.tables.row.employment_insurance': {
    ko: '고용보험', en: 'Employment insurance', ja: '雇用保険',
  },

  // ===== 6-month trend =====
  'payroll.trend.title': {
    ko: '최근 6개월 실수령액 추이',
    en: 'Net pay trend (last 6 months)',
    ja: '直近6ヶ月の実支給額推移',
  },
  'payroll.trend.bar_aria': {
    ko: '{label} 실수령 {amount}만원',
    en: '{label} net pay {amount}만 (10k won)',
    ja: '{label} 実支給 {amount}万円',
  },
  'payroll.trend.bar_value_suffix': { ko: '만', en: '0k', ja: '万' },
  'payroll.trend.bar_month_suffix': { ko: '월', en: '', ja: '月' },
};

export type PayrollI18nKey = keyof typeof MESSAGES;

/**
 * Cycle 68 — PayrollPage i18n lookup.
 * cycle 46 학습 phase 5 fallback chain (lang → en → ko) 적용.
 * params 치환 ({name}) — calendar/workspace/mail/onboarding/board/task/settings/chat 와 일관.
 */
export function tPayroll(key: PayrollI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

// base-i18n thin wrapper (cycle 41 통합 패턴 일관)
export function getPayrollLang(): Lang { return getSharedLang(); }
export function setPayrollLang(value: Lang): void { setSharedLang(value); }
export function subscribePayrollLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
